import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { AppDispatch, RootState } from "../Redux/Store";
import { allGet, allPost, allPut, allDelete } from "../Redux/Actions.tsx";
import { setMetodi } from "../Redux/Reducers/MetodoPagamentoReducer.ts";
import { setStati } from "../Redux/Reducers/StatoReducer.ts";
import { urlmetodi, urlstati } from "../lib/api.ts";
import { MetodoPagamento, Stato } from "../lib/interface.ts";
import { FaEdit, FaPlus, FaSave, FaTrashAlt } from "react-icons/fa";
import { useAlert } from "./Alert.tsx";
import "./SettaggiAvanzati.css";
import { ColorPicker } from "primereact/colorpicker";
import { Dialog } from "primereact/dialog";

const SettaggiAvanzati: React.FC = () => {
  const dispatch: AppDispatch = useDispatch();
  const { token } = useSelector((state: RootState) => state.login);
  const metodiDiPagamento = useSelector(
    (state: RootState) => state.metodi.metodi
  );

  // Stato per gli stati
  const stati = useSelector((state: RootState) => state.stati.stati);
  const [isDialogOpen, setDialogOpen] = useState(false);

  const [newMetodo, setNewMetodo] = useState("");
  const [editMetodo, setEditMetodo] = useState<MetodoPagamento | null>(null);

  const [newStato, setNewStato] = useState({ stato: "", colore: "" });
  const [editStato, setEditStato] = useState<Stato | null>(null);

  // Stati per controllare la visibilità delle tendine
  const [isMetodiDropdownOpen, setIsMetodiDropdownOpen] = useState(false);
  const [isStatiDropdownOpen, setIsStatiDropdownOpen] = useState(false);

  const showAlert = useAlert();

  useEffect(() => {
    dispatch(allGet(urlmetodi, token, setMetodi, showAlert));
    dispatch(allGet(urlstati, token, setStati, showAlert)); // Carica gli stati
  }, [dispatch, token]);

  // Funzioni per i metodi di pagamento
  const handleAddMetodo = () => {
    if (newMetodo.trim() === "") {
      showAlert(
        "danger",
        "Errore",
        "Il campo non può essere vuoto.",
        "bottom-end",
        3000
      );
      return;
    }

    dispatch(
      allPost(urlmetodi, token, { metodoPagamento: newMetodo }, showAlert)
    ).then((result) => {
      if (result) {
        setNewMetodo("");
        showAlert(
          "success",
          "Successo",
          "Metodo di pagamento aggiunto con successo.",
          "bottom-end",
          3000
        );
      }
    });
  };

  const handleUpdateMetodo = (metodo: MetodoPagamento) => {
    if (!metodo.metodoPagamento.trim()) {
      showAlert(
        "danger",
        "Errore",
        "Il campo non può essere vuoto.",
        "bottom-end",
        3000
      );
      return;
    }

    dispatch(
      allPut(`${urlmetodi}/${metodo.id}`, token, metodo, showAlert)
    ).then((result) => {
      if (result) {
        setEditMetodo(null);
        showAlert(
          "success",
          "Successo",
          "Metodo di pagamento aggiornato con successo.",
          "bottom-end",
          3000
        );
      }
    });
  };

  const handleDeleteMetodo = (id: string) => {
    dispatch(allDelete(`${urlmetodi}`, token, id, showAlert)).then((result) => {
      if (result) {
        showAlert(
          "warning",
          "Successo",
          "Metodo di pagamento eliminato con successo.",
          "bottom-end",
          3000
        );
      }
    });
  };

  // Funzioni per gli stati
  const handleAddStato = () => {
    if (newStato.stato.trim() === "") {
      showAlert(
        "danger",
        "Errore",
        "Il campo non può essere vuoto.",
        "bottom-end",
        3000
      );
      return;
    }

    dispatch(allPost(urlstati, token, newStato, showAlert)).then((result) => {
      if (result) {
        setNewStato({ stato: "", colore: "" });
        showAlert(
          "success",
          "Successo",
          "Stato aggiunto con successo.",
          "bottom-end",
          3000
        );
      }
    });
  };

  const handleUpdateStato = (stato: Stato) => {
    if (!stato.stato.trim()) {
      showAlert(
        "danger",
        "Errore",
        "Il campo non può essere vuoto.",
        "bottom-end",
        3000
      );
      return;
    }

    dispatch(allPut(`${urlstati}/${stato.id}`, token, stato, showAlert)).then(
      (result) => {
        if (result) {
          setEditStato(null);
          showAlert(
            "success",
            "Successo",
            "Stato aggiornato con successo.",
            "bottom-end",
            3000
          );
        }
      }
    );
  };

  const handleDeleteStato = (id: string) => {
    dispatch(allDelete(`${urlstati}`, token, id, showAlert)).then((result) => {
      if (result) {
        showAlert(
          "warning",
          "Successo",
          "Stato eliminato con successo.",
          "bottom-end",
          3000
        );
      }
    });
  };

  return (
    <Container className="personal-profile">
      <h5>Settaggi avanzati</h5>

      {/* Sezione per i Metodi di Pagamento */}
      <div className="custom-dropdown">
        <p
          className="dropdown-header"
          onClick={() => setIsMetodiDropdownOpen(!isMetodiDropdownOpen)}
          style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "0" }}
        >
          Metodi di pagamento
        </p>

        {isMetodiDropdownOpen && (
          <div className="custom-select-container">
            <ul className="custom-select-list">
              {metodiDiPagamento.map((metodo) => (
                <li
                  key={metodo.id}
                  className="custom-select-item d-flex align-items-center"
                >
                  {editMetodo?.id === metodo.id ? (
                    <Form.Control
                      type="text"
                      value={editMetodo?.metodoPagamento || ""}
                      onChange={(e) => {
                        if (editMetodo) {
                          setEditMetodo({
                            ...editMetodo,
                            metodoPagamento: e.target.value,
                          });
                        }
                      }}
                      className="flex-grow-1 me-2"
                    />
                  ) : (
                    <span className="flex-grow-1">
                      {metodo.metodoPagamento}
                    </span>
                  )}
                  {editMetodo?.id === metodo.id ? (
                    <>
                      <FaSave
                        className="icon-action me-2"
                        onClick={() => {
                          if (editMetodo) {
                            handleUpdateMetodo(editMetodo);
                          }
                        }}
                        title="Salva"
                        style={{ cursor: "pointer", color: "#ef5913" }}
                      />
                      <FaTrashAlt
                        className="icon-action"
                        onClick={() => handleDeleteMetodo(metodo.id)}
                        title="Elimina"
                        style={{ cursor: "pointer", color: "red" }}
                      />
                    </>
                  ) : (
                    <FaEdit
                      className="icon-action me-2"
                      onClick={() => setEditMetodo(metodo)}
                      title="Modifica"
                      style={{ cursor: "pointer", color: "#ef5913" }}
                    />
                  )}
                </li>
              ))}
              {/* Opzione per aggiungere un nuovo metodo */}
              <li className="custom-select-item d-flex align-items-center">
                <Form.Control
                  type="text"
                  value={newMetodo}
                  onChange={(e) => setNewMetodo(e.target.value)}
                  className="flex-grow-1"
                  placeholder="Nuovo metodo"
                />
                <FaPlus
                  className="icon-action ms-2"
                  onClick={handleAddMetodo}
                  title="Aggiungi"
                  style={{
                    cursor: "pointer",
                    color: "#ef5913",
                    fontSize: "1.2rem",
                  }}
                />
              </li>
            </ul>
          </div>
        )}
      </div>

      <div className="custom-dropdown mt-4">
        <p
          className="dropdown-header"
          onClick={() => setIsStatiDropdownOpen(!isStatiDropdownOpen)}
          style={{ cursor: "pointer", fontWeight: "bold", marginBottom: "0" }}
        >
          Stati
        </p>

        {isStatiDropdownOpen && (
          <div className="custom-select-container">
            <ul className="custom-select-list">
              {stati.map((stato) => (
                <li
                  key={stato.id}
                  className="custom-select-item d-flex align-items-center"
                >
                  {editStato?.id === stato.id ? (
                    <>
                      <Form.Control
                        type="text"
                        value={editStato?.stato || ""}
                        onChange={(e) => {
                          if (editStato) {
                            setEditStato({
                              ...editStato,
                              stato: e.target.value,
                            });
                          }
                        }}
                        className="flex-grow-1 me-2"
                      />
                      <ColorPicker
                        className="ms-2"
                        value={editStato?.colore}
                        onChange={(e) => {
                          if (editStato) {
                            setEditStato({ ...editStato, colore: e.value });
                          }
                        }}
                        style={{ width: "100%" }}
                      />
                    </>
                  ) : (
                    <>
                      <Form.Control
                        type="text"
                        value={stato.stato || ""}
                        disabled
                        onChange={(e) => {
                          if (editStato) {
                            setEditStato({
                              ...editStato,
                              stato: e.target.value,
                            });
                          }
                        }}
                        className="flex-grow-1 me-2"
                      />
                      <ColorPicker
                        className="ms-2"
                        disabled
                        value={stato.colore}
                        onChange={(e) => {
                          if (editStato) {
                            setEditStato({ ...editStato, colore: e.value });
                          }
                        }}
                        style={{ width: "100%" }}
                      />
                    </>
                  )}
                  {editStato?.id === stato.id ? (
                    <>
                      <FaSave
                        className="icon-action me-2"
                        onClick={() => {
                          if (editStato) {
                            handleUpdateStato(editStato);
                          }
                        }}
                        title="Salva"
                        style={{
                          cursor: "pointer",
                          color: "#ef5913",
                          fontSize: "2rem",
                        }}
                      />
                      {stati.length > 1 && (
                        <FaTrashAlt
                          className="icon-action"
                          onClick={() => handleDeleteStato(stato.id)}
                          title="Elimina"
                          style={{
                            cursor: "pointer",
                            color: "red",
                            fontSize: "2rem",
                          }}
                        />
                      )}
                    </>
                  ) : (
                    <FaEdit
                      className="icon-action me-2"
                      onClick={() => setEditStato(stato)}
                      title="Modifica"
                      style={{
                        cursor: "pointer",
                        color: "#ef5913",
                        fontSize: "2rem",
                      }}
                    />
                  )}
                </li>
              ))}

              <li className="custom-select-item d-flex align-items-center">
                <Row>
                  <Col xs="8">
                    <Form.Control
                      type="text"
                      name="nome"
                      value={newStato.stato}
                      onChange={(e) =>
                        setNewStato((prev) => ({
                          ...prev,
                          stato: e.target.value,
                        }))
                      }
                      className="flex-grow-1"
                      placeholder="Nuovo stato"
                    />
                  </Col>
                  <Col xs="2">
                    <ColorPicker
                      className="ms-2"
                      value={newStato.colore}
                      onChange={(e) =>
                        setNewStato((prev) => ({ ...prev, colore: e.value }))
                      }
                      style={{ width: "100%" }}
                    />
                  </Col>
                  <Col xs="2">
                    <FaPlus
                      className="icon-action ms-2"
                      onClick={handleAddStato}
                      title="Aggiungi"
                      style={{
                        cursor: "pointer",
                        color: "#ef5913",

                        margin: "0",
                      }}
                    />
                  </Col>
                </Row>
              </li>
            </ul>
          </div>
        )}
      </div>
    </Container>
  );
};

export default SettaggiAvanzati;
